import axios from "axios"
import { API_TIMEOUT_MS } from "constants/apiTimeout"
import { createHmacSha256, generateStrongKey } from "common/utils"
import { trimDeep } from "utils/formUtils"
import { redirectToMaintenance, isMaintenanceResponse } from "utils/maintenanceRedirect"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1/"
const HMAC_SECRET_KEY = process.env.REACT_APP_HMAC_SECRET_KEY || ""

export const PLAN_PAYMENT_TOKEN_KEY = "planPaymentToken"

const planPaymentApi = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT_MS,
})

const getStoredToken = () => {
  if (typeof sessionStorage === "undefined") {
    return null
  }
  return sessionStorage.getItem(PLAN_PAYMENT_TOKEN_KEY)
}

export const setPlanPaymentToken = token => {
  if (typeof sessionStorage !== "undefined" && token) {
    sessionStorage.setItem(PLAN_PAYMENT_TOKEN_KEY, token)
  }
}

export const getPlanPaymentTokenFromUrl = () => {
  if (typeof window === "undefined") {
    return ""
  }
  const params = new URLSearchParams(window.location.search)
  return params.get("token") || ""
}

planPaymentApi.interceptors.request.use(async config => {
  const token = getStoredToken()
  if (token) {
    config.headers.Authorization = token
  }

  const language =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("I18N_LANGUAGE")
      : null
  config.headers["Accept-Language"] = language === "sa" ? "ar" : "en"

  const method = config.method ? config.method.toUpperCase() : null
  if (method === "PUT" || method === "PATCH" || method === "POST") {
    try {
      if (config.data && typeof config.data === "object") {
        config.data = trimDeep(config.data)
      }
      const bodyString =
        config.data && typeof config.data === "object"
          ? JSON.stringify(config.data)
          : typeof config.data === "string"
            ? config.data
            : ""
      if (bodyString && HMAC_SECRET_KEY) {
        const hash = await createHmacSha256(HMAC_SECRET_KEY, bodyString)
        if (hash) {
          config.headers["X-Signature"] = hash
        }
      }
    } catch (_error) {
      // Non-fatal
    }
  } else if (method === "GET" || method === "DELETE") {
    try {
      const generatedKey = generateStrongKey()
      config.headers["x-st"] = generatedKey
      if (HMAC_SECRET_KEY) {
        const hash = await createHmacSha256(HMAC_SECRET_KEY, generatedKey)
        if (hash) {
          config.headers["X-Signature"] = hash
        }
      }
    } catch (_error) {
      // Non-fatal
    }
  }

  return config
})

planPaymentApi.interceptors.response.use(
  response => response,
  error => {
    if (isMaintenanceResponse(error)) {
      redirectToMaintenance(error)
    }
    return Promise.reject(error)
  }
)

export const getPlanPaymentApiMessage = res => {
  if (!res) {
    return ""
  }
  if (typeof res.message === "string" && res.message.trim()) {
    return res.message
  }
  if (typeof res.msg === "string" && res.msg.trim()) {
    return res.msg
  }
  return ""
}

export const planPaymentGet = (url, config = {}) =>
  planPaymentApi.get(url, config).then(res => res.data)

export const planPaymentPost = (url, data = {}, config = {}) =>
  planPaymentApi
    .post(url, data, config)
    .then(res => res.data)
    .catch(err => {
      if (err?.response?.data) {
        return err.response.data
      }
      throw err
    })
